import React from 'react';
import { Document, Page, View, Text as PdfText, StyleSheet } from '@react-pdf/renderer';
import { PDF_THEMES, DEFAULT_THEME } from '../themes';
import PdfHeader from '../components/PdfHeader';
import PdfFooter from '../components/PdfFooter';
import PdfSummaryCards from '../components/PdfSummaryCards';
import { PdfTableHead, PdfTableRow, PdfTableFoot } from '../components/PdfTable';
import { fmtINR, fmtDate, cap } from '../pdfUtils';

const s = StyleSheet.create({
  page:         { fontFamily: 'NotoSans', paddingBottom: 60 },
  content:      { marginHorizontal: 40, marginTop: 12 },
  infoBar:      { flexDirection: 'row', justifyContent: 'space-between', marginBottom: 8, paddingVertical: 5, paddingHorizontal: 6, borderRadius: 3 },
  sectionTitle: { fontSize: 9, fontWeight: 'bold', paddingVertical: 5, paddingHorizontal: 0, marginTop: 10 },
});

const BY_PERSON_COLS = [
  { label: 'Person',   flex: 2.5 },
  { label: 'Entries',  flex: 0.7, align: 'right' },
  { label: 'Offered',  flex: 1.2, align: 'right' },
  { label: 'Paid',     flex: 1.2, align: 'right' },
  { label: 'Pending',  flex: 1.2, align: 'right' },
];

const ENTRY_COLS = [
  { label: 'Person',     flex: 1.4 },
  { label: 'Category',   flex: 1.6 },
  { label: 'Date',       flex: 1 },
  { label: 'Offered',    flex: 1, align: 'right' },
  { label: 'Paid',       flex: 1, align: 'right' },
  { label: 'Pending',    flex: 1, align: 'right' },
  { label: 'Status',     flex: 0.9 },
];

const getCatName = (o) =>
  o._categoryName ||
  o.DonationCategory?.name ||
  (typeof o.donationCategoryId === 'object' ? o.donationCategoryId?.name : null) ||
  'â€”';

export default function OwnerLedgerDocument({ data, theme: themeKey, orientation, sections }) {
  const t = PDF_THEMES[themeKey] || PDF_THEMES[DEFAULT_THEME];
  const byPerson    = Array.isArray(data.byPerson)      ? data.byPerson      : [];
  const allEntries  = Array.isArray(data.ownerOfferings) ? data.ownerOfferings : [];

  const totOffered = allEntries.reduce((s, o) => s + (Number(o.offeredAmount) || 0), 0);
  const totPaid    = allEntries.reduce((s, o) => s + (Number(o.paidAmount)    || 0), 0);
  const totPending = allEntries.reduce((s, o) => s + (Number(o.pendingAmount) || 0), 0);

  const SectionTitle = ({ title }) => (
    <View style={[s.sectionTitle, { borderBottomWidth: 1, borderBottomcolor: t.body }]}>
      <PdfText style={{ fontSize: 9, fontWeight: 'bold', color: t.body }}>{title}</PdfText>
    </View>
  );

  return (
    <Document>
      <Page size="A4" orientation={orientation || 'landscape'} style={[s.page, { backgroundColor: t.white }]}>
        {sections.header && <PdfHeader derasar={data.derasar} reportTitle="Owner Ledger" theme={t} />}
        <View style={s.content}>

          {/* Owner name + FY info bar */}
          {(data.ownerName || data.fyLabel) && (
            <View style={[s.infoBar, { backgroundColor: t.light }]}>
              <PdfText style={{ fontSize: 9, fontWeight: 'bold', color: t.body }}>
                Owner: {data.ownerName || 'â€”'}
              </PdfText>
              <PdfText style={{ fontSize: 9, color: t.body }}>
                {data.fyLabel || ''}
              </PdfText>
            </View>
          )}

          {sections.summaryCards && (
            <PdfSummaryCards theme={t} items={[
              { label: 'Total Persons', value: String(byPerson.length),  color: t.primary },
              { label: 'Total Offered', value: fmtINR(totOffered),       color: t.body    },
              { label: 'Total Paid',    value: fmtINR(totPaid),          color: t.statusGreen },
              { label: 'Total Pending', value: fmtINR(totPending),       color: t.statusRed },
            ]} />
          )}

          {/* â”€â”€ By Person â”€â”€ */}
          <SectionTitle title="By Person" />
          <PdfTableHead cols={BY_PERSON_COLS} theme={t} />
          {byPerson.length === 0
            ? <PdfTableRow cols={BY_PERSON_COLS} index={0} theme={t} values={['No data found', '', '', '', '']} />
            : byPerson.map((row, i) => (
                <PdfTableRow key={i} cols={BY_PERSON_COLS} index={i} theme={t}
                  values={[row.name || 'â€”', String(row.count || 0), fmtINR(row.pledged), fmtINR(row.paid), fmtINR(row.pending)]}
                />
              ))
          }
          {byPerson.length > 0 && (
            <PdfTableFoot cols={BY_PERSON_COLS}
              values={[`Total (${byPerson.length} persons)`, String(allEntries.length), fmtINR(totOffered), fmtINR(totPaid), fmtINR(totPending)]}
              theme={t}
            />
          )}

          {/* â”€â”€ All Offering Entries â”€â”€ */}
          <SectionTitle title="All Offering Entries" />
          <PdfTableHead cols={ENTRY_COLS} theme={t} />
          {allEntries.length === 0
            ? <PdfTableRow cols={ENTRY_COLS} index={0} theme={t} values={['No entries found', '', '', '', '', '', '']} />
            : allEntries.map((row, i) => (
                <PdfTableRow key={i} cols={ENTRY_COLS} index={i} theme={t}
                  values={[
                    row.member?.name || row.userId?.name || 'â€”',
                    getCatName(row),
                    fmtDate(row.offeringDate),
                    fmtINR(row.offeredAmount),
                    fmtINR(row.paidAmount),
                    fmtINR(row.pendingAmount),
                    cap(row.status),
                  ]}
                />
              ))
          }
          {allEntries.length > 0 && (
            <PdfTableFoot cols={ENTRY_COLS}
              values={[`Total (${allEntries.length} entries)`, '', '', fmtINR(totOffered), fmtINR(totPaid), fmtINR(totPending), '']}
              theme={t}
            />
          )}
        </View>
        {sections.footer && <PdfFooter theme={t} />}
      </Page>
    </Document>
  );
}



